function objectValue(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function hasValue(value) {
  return value !== undefined && value !== null && !(typeof value === "string" && value.trim() === "");
}

function cloneValue(value) {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
}

function mergeMissing(source, defaults) {
  const output = { ...objectValue(source) };
  Object.entries(defaults).forEach(([key, value]) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      output[key] = mergeMissing(output[key], value);
    } else if (!hasValue(output[key])) {
      output[key] = cloneValue(value);
    }
  });
  return output;
}

export const HIGHLIGHT_VISUAL_DEFAULTS = Object.freeze({
  cardStyle: Object.freeze({
    backgroundColor: "#FFFFFF",
    borderColor: "rgba(26,36,47,.10)",
    borderWidth: 1,
    borderRadius: 16,
    boxShadow: "none",
    hoverBoxShadow: "0 16px 36px rgba(20,26,38,.11)",
    hoverTranslateY: -4,
    contentPadding: 24,
  }),
  iconStyle: Object.freeze({ color: "#F35A3A", size: 28 }),
  iconSurfaceStyle: Object.freeze({
    backgroundColor: "rgba(25,36,47,.05)",
    floatingBackgroundColor: "#FFFFFF",
    borderColor: "rgba(26,36,47,.10)",
    borderWidth: 1,
    borderRadius: 12,
    width: 54,
    height: 54,
    mediaWidth: 48,
    mediaHeight: 48,
    mediaBorderRadius: 10,
    boxShadow: "none",
    floatingBoxShadow: "0 8px 22px rgba(20,26,38,.12)",
  }),
  titleStyle: Object.freeze({ color: "#19242F", fontSize: 20, fontWeight: 900, lineHeight: 1.25 }),
  descriptionStyle: Object.freeze({ color: "#5B5D73", fontSize: 16, fontWeight: 400, lineHeight: 1.7 }),
  imageStyle: Object.freeze({ backgroundColor: "rgba(25,36,47,.06)", objectFit: "cover", height: 190 }),
  videoStyle: Object.freeze({ backgroundColor: "#000000", aspectRatio: "16 / 9" }),
});

export function materializeHighlightItemVisual(rawItem = {}) {
  const item = { ...objectValue(rawItem) };
  const cardStyle = { ...objectValue(item.cardStyle) };
  const iconStyle = { ...objectValue(item.iconStyle) };
  const titleStyle = { ...objectValue(item.titleStyle) };
  const descriptionStyle = { ...objectValue(item.descriptionStyle) };

  if (!hasValue(cardStyle.backgroundColor) && hasValue(item.backgroundColor)) cardStyle.backgroundColor = item.backgroundColor;
  if (!hasValue(iconStyle.color) && hasValue(item.iconColor)) iconStyle.color = item.iconColor;
  if (!hasValue(titleStyle.color) && hasValue(item.titleColor)) titleStyle.color = item.titleColor;
  if (!hasValue(descriptionStyle.color) && hasValue(item.descriptionColor)) descriptionStyle.color = item.descriptionColor;

  item.cardStyle = mergeMissing(cardStyle, HIGHLIGHT_VISUAL_DEFAULTS.cardStyle);
  item.iconStyle = mergeMissing(iconStyle, HIGHLIGHT_VISUAL_DEFAULTS.iconStyle);
  item.iconSurfaceStyle = mergeMissing(item.iconSurfaceStyle, {
    ...HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle,
    floatingBackgroundColor: item.cardStyle.backgroundColor,
  });
  item.titleStyle = mergeMissing(titleStyle, HIGHLIGHT_VISUAL_DEFAULTS.titleStyle);
  item.descriptionStyle = mergeMissing(descriptionStyle, HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle);
  item.imageStyle = mergeMissing(item.imageStyle, HIGHLIGHT_VISUAL_DEFAULTS.imageStyle);
  item.videoStyle = mergeMissing(item.videoStyle, HIGHLIGHT_VISUAL_DEFAULTS.videoStyle);

  delete item.backgroundColor;
  delete item.iconColor;
  delete item.titleColor;
  delete item.descriptionColor;
  return item;
}

export function materializeHighlightItemsVisual(items = []) {
  return (Array.isArray(items) ? items : []).map(materializeHighlightItemVisual);
}

export function materializeHighlightsOwnership(content = {}) {
  const source = cloneValue(objectValue(content));
  if (Array.isArray(source.highlights)) source.highlights = materializeHighlightItemsVisual(source.highlights);
  if (Array.isArray(source.layout?.segments)) {
    source.layout = {
      ...source.layout,
      segments: source.layout.segments.map((segment) => {
        if (!segment || segment.type !== "highlights" || !segment.data) return segment;
        const data = cloneValue(objectValue(segment.data));
        if (Array.isArray(data.highlights)) data.highlights = materializeHighlightItemsVisual(data.highlights);
        return { ...segment, data };
      }),
    };
  }
  return source;
}
